/**
 * Vendors Management Page
 * 
 * Full CRUD for vendors with CSV import/export.
 * Uses the same vendor table as Purchase Orders dropdown.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Building2, Plus, Upload, Download, Search, MoreHorizontal, Pencil, Trash2, ToggleLeft, ToggleRight, FileDown, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { vendorsAPI, warehouseAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { AppDrawer } from '../components/ui/AppDrawer';
import { MobileFABContainer, MobileFABMenu, MobileFABMenuItem } from '../components/MobileFAB';
import { KebabMenu } from '../components/KebabMenu';
import { useSortableTable } from '../hooks/useSortableTable';
import { SortableTableHead } from '../components/ui/SortableTableHead';

const VENDOR_SORT_COLUMNS = [
  { key: 'name', type: 'text' },
  { key: 'is_active', type: 'status' },
];

const Vendors = () => {
  // State
  const [vendors, setVendors] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  
  // Modal states
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  
  // Controlled kebab menu state (prevents Radix overlay conflict with drawer)
  const [openMenuId, setOpenMenuId] = useState(null);
  const [fabOpen, setFabOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    contact_name: '',
    email: '',
    phone: '',
    payment_terms: '',
    payment_terms_type: '',
    payment_terms_days: '',
    default_warehouse_id: '',
    notes: '',
    is_active: true,
  });
  
  // Import state
  const [importFile, setImportFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  
  // Delete dialog state
  const [deleteInfo, setDeleteInfo] = useState(null);

  // Load data
  const loadVendors = useCallback(async () => {
    try {
      setLoading(true);
      const response = await vendorsAPI.list({ search, include_inactive: showInactive });
      setVendors(response.data);
    } catch (error) {
      toast.error('Failed to load vendors');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [search, showInactive]);

  const loadWarehouses = useCallback(async () => {
    try {
      const response = await warehouseAPI.listWarehouses();
      setWarehouses(response.data.filter(w => w.is_active));
    } catch (error) {
      console.error('Failed to load warehouses:', error);
    }
  }, []);

  useEffect(() => {
    loadVendors();
    loadWarehouses();
  }, [loadVendors, loadWarehouses]);

  // Handlers
  const handleOpenCreate = () => {
    setSelectedVendor(null);
    setFormData({
      name: '',
      contact_name: '',
      email: '',
      phone: '',
      payment_terms: '',
      payment_terms_type: '',
      payment_terms_days: '',
      default_warehouse_id: '',
      notes: '',
      is_active: true,
    });
    setEditModalOpen(true);
  };

  const handleOpenEdit = (vendor) => {
    setOpenMenuId(null);
    setTimeout(() => {
      setSelectedVendor(vendor);
      setFormData({
        name: vendor.name || '',
        contact_name: vendor.contact_name || '',
        email: vendor.email || '',
        phone: vendor.phone || '',
        payment_terms: vendor.payment_terms || '',
        payment_terms_type: vendor.payment_terms_type || '',
        payment_terms_days: vendor.payment_terms_days ?? '',
        default_warehouse_id: vendor.default_warehouse_id || '',
        notes: vendor.notes || '',
        is_active: vendor.is_active,
      });
      setEditModalOpen(true);
    }, 0);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error('Vendor name is required');
      return;
    }
    
    // Validate email format if provided
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        default_warehouse_id: formData.default_warehouse_id || null,
        payment_terms_type: formData.payment_terms_type || null,
        payment_terms_days: formData.payment_terms_type === 'custom' && formData.payment_terms_days
          ? parseInt(formData.payment_terms_days, 10) : null,
      };
      
      if (selectedVendor) {
        await vendorsAPI.update(selectedVendor.id, payload);
        toast.success('Vendor updated');
      } else {
        await vendorsAPI.create(payload);
        toast.success('Vendor created');
      }
      setEditModalOpen(false);
      loadVendors();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to save vendor';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (vendor) => {
    setSelectedVendor(vendor);
    setDeleteInfo(null);
    setDeleteDialogOpen(true);
    
    // Check if vendor can be deleted
    try {
      const response = await vendorsAPI.delete(vendor.id, false);
      if (response.data.requires_deactivate) {
        setDeleteInfo(response.data);
      }
    } catch (error) {
      // If error, just show the dialog anyway
    }
  };

  const confirmDelete = async (forceDeactivate = false) => {
    if (!selectedVendor) return;
    
    try {
      const response = await vendorsAPI.delete(selectedVendor.id, forceDeactivate);
      if (response.data.deleted) {
        toast.success('Vendor deleted');
      } else if (response.data.deactivated) {
        toast.success('Vendor deactivated');
      }
      setDeleteDialogOpen(false);
      loadVendors();
    } catch (error) {
      toast.error('Failed to delete vendor');
    }
  };

  const handleToggleActive = async (vendor) => {
    try {
      await vendorsAPI.update(vendor.id, { is_active: !vendor.is_active });
      toast.success(vendor.is_active ? 'Vendor deactivated' : 'Vendor activated');
      loadVendors();
    } catch (error) {
      toast.error('Failed to update vendor');
    }
  };

  // CSV handlers
  const handleExport = async () => {
    try {
      const response = await vendorsAPI.exportCSV({ include_inactive: true });
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vendors_export_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export downloaded');
    } catch (error) {
      toast.error('Failed to export vendors');
    }
  };

  const handleDownloadTemplate = async () => {
    try {
      const response = await vendorsAPI.downloadTemplate();
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'vendor_import_template.csv';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleImport = async () => {
    if (!importFile) {
      toast.error('Please select a CSV file');
      return;
    }

    setImporting(true);
    setImportResult(null);
    try {
      const response = await vendorsAPI.importCSV(importFile);
      setImportResult(response.data);
      if (response.data.created > 0 || response.data.updated > 0) {
        toast.success(`Import complete: ${response.data.created} created, ${response.data.updated} updated`);
        loadVendors();
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Import failed';
      toast.error(message);
    } finally {
      setImporting(false);
    }
  };

  const closeImportModal = () => {
    setImportModalOpen(false);
    setImportFile(null);
    setImportResult(null);
  };

  // Filtered vendors (search is done server-side, but we can do local filtering too)
  const filteredVendors = vendors;
  const { sorted: sortedVendors, sortKey, sortDir, onSort } = useSortableTable(filteredVendors, { columns: VENDOR_SORT_COLUMNS });

  return (
    <Layout>
    <div className="space-y-6 w-full min-w-0 max-w-full overflow-hidden" data-testid="vendors-page">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="vendors-title">
            <Building2 className="w-6 h-6" />
            Vendors
          </h1>
          <p className="text-sm text-muted-foreground mt-1 hidden sm:block">
            Manage your supplier and vendor directory
          </p>
        </div>
        {/* Desktop buttons */}
        <div className="hidden md:flex items-center gap-2">
          <Button variant="outline" onClick={() => setImportModalOpen(true)} data-testid="import-csv-btn">
            <Upload className="w-4 h-4 mr-2" />
            Import CSV
          </Button>
          <Button variant="outline" onClick={handleExport} data-testid="export-csv-btn">
            <Download className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          <Button onClick={handleOpenCreate} data-testid="add-vendor-btn">
            <Plus className="w-4 h-4 mr-2" />
            Add Vendor
          </Button>
        </div>
      </div>

      {/* Filters */}
      {/* Mobile: toggle above search */}
      <div className="md:hidden flex items-center gap-2 mb-2">
        <Switch
          checked={showInactive}
          onCheckedChange={setShowInactive}
          id="show-inactive-mobile"
          data-testid="show-inactive-toggle"
        />
        <Label htmlFor="show-inactive-mobile" className="text-sm text-muted-foreground">
          Show inactive
        </Label>
      </div>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center">
        <div className="flex items-center gap-2 flex-1">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              placeholder="Search vendors..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
              data-testid="vendor-search-input"
            />
          </div>
          {/* Mobile kebab for Export */}
          <div className="md:hidden">
            <KebabMenu
              items={[{ label: 'Export CSV', icon: Download, onClick: handleExport, 'data-testid': 'vendor-export-kebab' }]}
              data-testid="vendor-kebab-menu"
            />
          </div>
        </div>
        {/* Desktop: toggle next to search */}
        <div className="hidden md:flex items-center gap-2">
          <Switch
            checked={showInactive}
            onCheckedChange={setShowInactive}
            id="show-inactive"
            data-testid="show-inactive-toggle-desktop"
          />
          <Label htmlFor="show-inactive" className="text-sm text-muted-foreground">
            Show inactive
          </Label>
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="name" onSort={onSort}>Details</SortableTableHead>
              <SortableTableHead sortKey={sortKey} sortDir={sortDir} columnKey="is_active" onSort={onSort} className="w-24">Status</SortableTableHead>
              <TableHead className="text-right w-20">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8">
                  <Loader2 className="w-6 h-6 animate-spin mx-auto text-muted-foreground" />
                </TableCell>
              </TableRow>
            ) : sortedVendors.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center py-8 text-muted-foreground">
                  {search ? 'No vendors found matching your search' : 'No vendors yet. Add your first vendor!'}
                </TableCell>
              </TableRow>
            ) : (
              sortedVendors.map((vendor) => (
                <TableRow 
                  key={vendor.id} 
                  className={!vendor.is_active ? 'opacity-60' : ''}
                  data-testid={`vendor-row-${vendor.id}`}
                >
                  <TableCell>
                    {/* Line 1: Vendor Name (dominant) + PO count */}
                    <div className="flex items-baseline gap-2">
                      <span className="text-base font-semibold">{vendor.name}</span>
                      {vendor.po_count > 0 && (
                        <span className="text-xs text-muted-foreground">
                          ({vendor.po_count} PO{vendor.po_count !== 1 ? 's' : ''})
                        </span>
                      )}
                    </div>
                    {/* Line 2: Contact name (if present) */}
                    {vendor.contact_name && (
                      <div className="text-sm font-medium text-foreground mt-0.5">
                        {vendor.contact_name}
                      </div>
                    )}
                    {/* Line 3: Phone · Email · Payment Terms · Default Warehouse */}
                    {(() => {
                      const parts = [
                        vendor.phone || null,
                        vendor.email || null,
                        vendor.payment_terms ? `Terms: ${vendor.payment_terms}` : null,
                        vendor.default_warehouse_name || null,
                      ].filter(Boolean);
                      return parts.length > 0 ? (
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {parts.join(' · ')}
                        </div>
                      ) : null;
                    })()}
                  </TableCell>
                  <TableCell>
                    <Badge variant={vendor.is_active ? 'default' : 'secondary'}>
                      {vendor.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <DropdownMenu open={openMenuId === vendor.id} onOpenChange={(open) => setOpenMenuId(open ? vendor.id : null)}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" data-testid={`vendor-actions-${vendor.id}`}>
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleOpenEdit(vendor)}>
                          <Pencil className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => { setOpenMenuId(null); handleToggleActive(vendor); }}>
                          {vendor.is_active ? (
                            <>
                              <ToggleLeft className="w-4 h-4 mr-2" />
                              Deactivate
                            </>
                          ) : (
                            <>
                              <ToggleRight className="w-4 h-4 mr-2" />
                              Activate
                            </>
                          )}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem 
                          onClick={() => { setOpenMenuId(null); handleDelete(vendor); }}
                          className="text-destructive"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Add/Edit Vendor Drawer */}
      <AppDrawer
        open={editModalOpen}
        onOpenChange={(open) => {
          setEditModalOpen(open);
          if (!open) {
            setSelectedVendor(null);
          }
        }}
        title={selectedVendor ? 'Edit Vendor' : 'Add Vendor'}
        description={selectedVendor ? 'Update vendor information' : 'Add a new vendor to your directory'}
        size="md"
        isDirty={!selectedVendor && !!(formData.name || formData.email || formData.phone || formData.contact_name || formData.notes)}
        data-testid="vendor-drawer"
        footer={
          <>
            <Button variant="outline" onClick={() => setEditModalOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} data-testid="vendor-save-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {selectedVendor ? 'Save Changes' : 'Create Vendor'}
            </Button>
          </>
        }
      >
        <div className="grid gap-4">
            <div className="grid gap-2">
              <Label htmlFor="name">Vendor Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter vendor name"
                data-testid="vendor-name-input"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                placeholder="Primary contact person"
                data-testid="vendor-contact-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="vendor@example.com"
                  data-testid="vendor-email-input"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="555-123-4567"
                  data-testid="vendor-phone-input"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="payment_terms_type">Payment Terms</Label>
                <Select
                  value={formData.payment_terms_type || "none"}
                  onValueChange={(value) => {
                    const pt = value === "none" ? "" : value;
                    const labels = { net_15: "Net 15", net_30: "Net 30", net_45: "Net 45", net_60: "Net 60", due_on_receipt: "Due on Receipt" };
                    setFormData({
                      ...formData,
                      payment_terms_type: pt,
                      payment_terms: labels[pt] || (pt === "custom" ? formData.payment_terms : ""),
                      payment_terms_days: pt === "custom" ? formData.payment_terms_days : "",
                    });
                  }}
                >
                  <SelectTrigger data-testid="vendor-payment-terms-select">
                    <SelectValue placeholder="Select terms" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    <SelectItem value="net_15">Net 15</SelectItem>
                    <SelectItem value="net_30">Net 30</SelectItem>
                    <SelectItem value="net_45">Net 45</SelectItem>
                    <SelectItem value="net_60">Net 60</SelectItem>
                    <SelectItem value="due_on_receipt">Due on Receipt</SelectItem>
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
                {formData.payment_terms_type === 'custom' && (
                  <Input
                    type="number"
                    min={1}
                    placeholder="Days (e.g. 20)"
                    value={formData.payment_terms_days}
                    onChange={(e) => {
                      const d = e.target.value;
                      setFormData({ ...formData, payment_terms_days: d, payment_terms: d ? `Net ${d}` : 'Custom' });
                    }}
                    className="mt-1"
                    data-testid="vendor-custom-days-input"
                  />
                )}
              </div>
              <div className="grid gap-2">
                <Label htmlFor="default_warehouse">Default Warehouse</Label>
                <Select
                  value={formData.default_warehouse_id || "none"}
                  onValueChange={(value) => setFormData({ ...formData, default_warehouse_id: value === "none" ? "" : value })}
                >
                  <SelectTrigger data-testid="vendor-warehouse-select">
                    <SelectValue placeholder="Select warehouse" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {warehouses.map((wh) => (
                      <SelectItem key={wh.id} value={wh.id}>
                        {wh.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this vendor..."
                rows={3}
                data-testid="vendor-notes-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Switch
                id="is_active"
                checked={formData.is_active}
                onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                data-testid="vendor-active-toggle"
              />
              <Label htmlFor="is_active">Active</Label>
            </div>
        </div>
      </AppDrawer>

      {/* Import Modal */}
      <Dialog open={importModalOpen} onOpenChange={closeImportModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Import Vendors from CSV</DialogTitle>
            <DialogDescription>
              Upload a CSV file to bulk import vendors. Existing vendors will be updated by name match.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="flex justify-end">
              <Button variant="link" size="sm" onClick={handleDownloadTemplate} className="gap-2">
                <FileDown className="w-4 h-4" />
                Download Template CSV
              </Button>
            </div>
            <div className="grid gap-2">
              <Label htmlFor="csv-file">CSV File</Label>
              <Input
                id="csv-file"
                type="file"
                accept=".csv"
                onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                data-testid="import-file-input"
              />
            </div>
            {importResult && (
              <div className="p-4 bg-muted rounded-lg space-y-2">
                <h4 className="font-medium">Import Results</h4>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Created:</span>{' '}
                    <span className="font-medium text-green-600">{importResult.created}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Updated:</span>{' '}
                    <span className="font-medium text-blue-600">{importResult.updated}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Failed:</span>{' '}
                    <span className="font-medium text-red-600">{importResult.failed}</span>
                  </div>
                </div>
                {importResult.errors?.length > 0 && (
                  <div className="mt-2">
                    <h5 className="text-sm font-medium text-red-600">Errors:</h5>
                    <ul className="text-xs text-muted-foreground max-h-32 overflow-y-auto">
                      {importResult.errors.slice(0, 10).map((err, i) => (
                        <li key={i}>Row {err.row}: {err.error}</li>
                      ))}
                      {importResult.errors.length > 10 && (
                        <li>...and {importResult.errors.length - 10} more</li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeImportModal}>
              {importResult ? 'Close' : 'Cancel'}
            </Button>
            {!importResult && (
              <Button onClick={handleImport} disabled={importing || !importFile} data-testid="import-submit-btn">
                {importing && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Import
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {deleteInfo?.requires_deactivate ? 'Cannot Delete Vendor' : 'Delete Vendor'}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {deleteInfo?.requires_deactivate ? (
                <>
                  <p className="mb-2">{deleteInfo.message}</p>
                  <p>Would you like to deactivate this vendor instead? Deactivated vendors are hidden from lists but their PO history is preserved.</p>
                </>
              ) : (
                <>
                  Are you sure you want to delete "{selectedVendor?.name}"? This action cannot be undone.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            {deleteInfo?.requires_deactivate ? (
              <AlertDialogAction onClick={() => confirmDelete(true)}>
                Deactivate
              </AlertDialogAction>
            ) : (
              <AlertDialogAction 
                onClick={() => confirmDelete(false)}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                Delete
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Mobile FAB */}
      <MobileFABContainer data-testid="vendor-fab-container">
        <MobileFABMenu open={fabOpen} onClose={() => setFabOpen(false)}>
          <MobileFABMenuItem icon={Plus} onClick={() => { setFabOpen(false); handleOpenCreate(); }}>
            Add Vendor
          </MobileFABMenuItem>
          <MobileFABMenuItem icon={Upload} onClick={() => { setFabOpen(false); setImportModalOpen(true); }}>
            Import CSV
          </MobileFABMenuItem>
        </MobileFABMenu>
        <button
          className="w-12 h-12 bg-primary text-primary-foreground rounded-full shadow-lg flex items-center justify-center hover:bg-primary/90 transition-all active:scale-95"
          onClick={() => setFabOpen(!fabOpen)}
          data-testid="vendor-mobile-fab"
        >
          <Plus className={`w-5 h-5 transition-transform ${fabOpen ? 'rotate-45' : ''}`} strokeWidth={2.5} />
        </button>
      </MobileFABContainer>
    </div>
    </Layout>
  );
};

export default Vendors;
